import "./styles.scss";

import { useState } from "react";
import Popup from "reactjs-popup";
import { FaCheck } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { handleSelectColor } from "../../../utils/utils";
import { setNextContentsStudent } from "../../../store/slices/methodology.slice";

const CardClassesHeld = ({ contentSelected, setContentSelected }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { nextContentsStudent, currentCycleAndLevelStudent } = useSelector((state) => state.methodologyReducer);

  const isDemoOrFirstClass = currentCycleAndLevelStudent.length === 0;

  const [moreClassLimit, setMoreClassLimit] = useState(5);

  const handleMoreContent = () => setMoreClassLimit(10);

  const handleContentSelected = (id, index, demo = false) => {
    const isAlreadySelected = contentSelected.includes(id);

    if (demo) {
      if (isAlreadySelected) {
        setContentSelected(contentSelected.filter((item) => item !== id));
        dispatch(
          setNextContentsStudent(
            nextContentsStudent.map((item) => (item.id === id ? { ...item, checkbox: false } : item)),
          ),
        );
      } else {
        setContentSelected([...contentSelected, id]);
        dispatch(
          setNextContentsStudent(
            nextContentsStudent.map((item) => (item.id === id ? { ...item, checkbox: true } : item)),
          ),
        );
      }
    } else {
      const isNextSelected = nextContentsStudent[index + 1]?.checkbox;
      if (isAlreadySelected && isNextSelected) return;

      const updatedContentSelected = isAlreadySelected
        ? contentSelected.filter((item) => item !== id)
        : [...contentSelected, id];

      setContentSelected(updatedContentSelected);

      dispatch(
        setNextContentsStudent(
          nextContentsStudent.map((item, i) => {
            if (item.id === id) {
              return { ...item, classNotCompleted: false, checkbox: !isAlreadySelected };
            }
            if (i === index - 1 && !isAlreadySelected) {
              return { ...item, pageFinished: "", continueComment: "", classNotCompleted: false };
            }
            return item;
          }),
        ),
      );
    }
  };

  const handleClassNotCompleted = (id) => {
    dispatch(
      setNextContentsStudent(
        nextContentsStudent.map((item) =>
          item.id === id ? { ...item, classNotCompleted: !item.classNotCompleted } : item,
        ),
      ),
    );
  };

  const handlePageFinished = (id, page) => {
    dispatch(
      setNextContentsStudent(
        nextContentsStudent.map((item) => (item.id === id ? { ...item, pageFinished: page } : item)),
      ),
    );
  };

  const handleContinueComment = (id, comment) => {
    dispatch(
      setNextContentsStudent(
        nextContentsStudent.map((item) => (item.id === id ? { ...item, continueComment: comment } : item)),
      ),
    );
  };

  const handleCheckboxDisabled = (index, demo) => {
    if (demo) return false;
    return index > 0 && !nextContentsStudent[index - 1]?.checkbox;
  };

  const renderPopup = (value, max) => (
    <Popup
      on={"hover"}
      position={"top center"}
      trigger={<p className="descriptionContent">{value.slice(0, max).concat("...")}</p>}
      style={{ width: 300, zIndex: 999999999, marginLeft: 20 }}
    >
      <div>
        <p>{value}</p>
      </div>
    </Popup>
  );

  return (
    <div>
      <div className="wrapperClasses">
        <h3>{t("CLASSES_HELD")}</h3>

        {nextContentsStudent?.slice(0, moreClassLimit)?.map((item, index) => (
          <div key={index} className="row">
            <div className="divPrimary">
              <div
                onClick={() => handleContentSelected(item?.id, index, item.level === "DEMO")}
                className={`checkbox ${handleCheckboxDisabled(index, item.level === "DEMO") ? "disabled" : ""}`}
              >
                {item.checkbox && <FaCheck size={17} color="#5E77F3" />}
              </div>

              {item.linkForTeacher && (
                <div className="buttonCodeContent">
                  <a href={item.linkForTeacher} target="_blank" rel="noreferrer">
                    <span>{item.contentCode}</span>
                  </a>
                </div>
              )}

              {item?.focus?.toLowerCase() === "specific" && (
                <>
                  <div className="buttonCodeContent">
                    <a href={item.linkForTeacher} target="_blank" rel="noreferrer">
                      <span className="specific">{t("BTN_SPECIFIC")}</span>
                    </a>
                  </div>
                  {item?.specificSubject && (
                    <div>
                      <label className="titleContent">{t("STUDENT_DESCRIPTION")}</label>
                      <p className="descriptionContent">{item?.specificSubject}</p>
                    </div>
                  )}
                </>
              )}

              {(item.classTitle || item.cycleName) && (
                <div className="wrapperContent">
                  <div className="w-104">
                    <label className="titleContent">{t("CLASS_TITLE")}</label>
                    {renderPopup(item.classTitle, 10)}
                  </div>

                  <div>
                    <label className="titleContent">{t("CYCLE_NAME")}</label>
                    {renderPopup(item.cycleName, 25)}
                  </div>
                </div>
              )}

              {item.level && (
                <div className={`cycleLevel ${handleSelectColor(item.level)}`}>
                  <span>{item.level === "DEMO" ? "D" : item.level}</span>
                </div>
              )}

              {item.level && (
                <div className="cycle">
                  <label className="labelCycle">{t("CYCLE")}</label>
                  <div className={`circle ${handleSelectColor(item.level)}`}>
                    <span>{item.cycle}</span>
                  </div>
                </div>
              )}
            </div>

            {item?.focus?.toLowerCase() !== "specific" && !isDemoOrFirstClass && item.level !== "DEMO" && (
              <div className="divSecondary">
                {item.checkbox && !nextContentsStudent[index + 1]?.checkbox && (
                  <div className="wraperCheckbox" onClick={() => handleClassNotCompleted(item?.id)}>
                    <div className="checkbox">{item.classNotCompleted && <FaCheck size={17} color="#5E77F3" />}</div>
                    <label>{t("CLASS_NOT_COMPLETED")}</label>
                  </div>
                )}

                {item.classNotCompleted && item.checkbox && !nextContentsStudent[index + 1]?.checkbox && (
                  <>
                    <div className="wraperBox">
                      <input
                        className="box"
                        value={item.pageFinished}
                        onChange={(e) => handlePageFinished(item?.id, e.target.value)}
                      />
                      <label>{t("PAGE")}</label>
                    </div>

                    <div className="inputSpecific">
                      <label>{t("SPECIFY_NEXT_TEACHER")}</label>
                      <input
                        maxLength={45}
                        value={item?.continueComment}
                        onChange={(e) => handleContinueComment(item?.id, e.target.value)}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {nextContentsStudent[4]?.checkbox && (
        <div className="buttonMoreClass" onClick={handleMoreContent}>
          <button>{t("MORE_CLASSES")}</button>
        </div>
      )}
    </div>
  );
};

export default CardClassesHeld;
